import { message }    from '@widesk-ui/hooks/useMessage';
import { UploadFile } from 'antd/es/upload';
import { RcFile }     from 'antd/es/upload';

export function convertAspectStringToFloat(aspect: string): number {
	const numbers = aspect.split('/');
	if (numbers.length !== 2 || !numbers[0] || !numbers[1]) {
		message.error(`L'aspect de l'image n'est pas correctement formaté.`);
		return 1;
	}

	return parseInt(numbers[0]) / parseInt(numbers[1]);
}

export function getFileDimension(file: File): Promise<{ width: number, height: number }> {
	return new Promise(resolve => {
		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.addEventListener('load', event => {
			const _loadedImageUrl = event.target?.result;
			const image = document.createElement('img');
			image.src = _loadedImageUrl as string;

			image.addEventListener('load', () => {
				const { width, height } = image;
				resolve({ width, height });
			});
		});
	});
}

export function toBase64(file: UploadFile | File): Promise<string> {
	return new Promise((resolve, reject) => {
			const reader = new FileReader();

			reader.readAsDataURL(file as RcFile);
			reader.onload = () => resolve(reader.result as string);
			reader.onerror = (error) => reject(error);
		},
	);
}

// Paliers de qualité essayés quand une image ré-encodée est plus lourde que l'originale.
const LOSSY_QUALITIES = [0.92, 0.9, 0.85, 0.8, 0.75, 0.7];
const LOSSY_TYPES = ['image/jpeg', 'image/webp'];

export function canvasToBlob(canvas: HTMLCanvasElement, type?: string, quality?: number): Promise<Blob> {
	return new Promise((resolve, reject) => {
		canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error(`Impossible d'encoder l'image`)), type, quality);
	});
}

function drawToCanvas(image: ImageBitmap, maxSize?: number): HTMLCanvasElement {
	const scale = maxSize ? Math.min(1, maxSize / Math.max(image.width, image.height)) : 1;
	const canvas = document.createElement('canvas');
	canvas.width = Math.max(1, Math.round(image.width * scale));
	canvas.height = Math.max(1, Math.round(image.height * scale));
	canvas.getContext('2d')?.drawImage(image, 0, 0, canvas.width, canvas.height);

	return canvas;
}

// Vrai si les deux images ont le même contenu, aux écarts de ré-encodage près
// (image validée dans l'éditeur sans recadrage, rotation ni déplacement).
function haveSameContent(a: ImageBitmap, b: ImageBitmap): boolean {
	if (a.width !== b.width || a.height !== b.height) return false;

	const getPixels = (image: ImageBitmap) => {
		const canvas = drawToCanvas(image, 1024);
		return canvas.getContext('2d')?.getImageData(0, 0, canvas.width, canvas.height).data;
	};
	const pixelsA = getPixels(a);
	const pixelsB = getPixels(b);
	if (!pixelsA || !pixelsB) return false;

	// Un ré-encodage JPEG/WebP s'écarte en moyenne de moins de 2 niveaux et presque jamais de plus de 64 ;
	// un décalage de 2 px, une rotation ou un réglage de luminosité dépassent nettement l'un des deux seuils.
	let totalDifference = 0;
	let largeDifferences = 0;
	for (let i = 0; i < pixelsA.length; i++) {
		const difference = Math.abs(pixelsA[i]! - pixelsB[i]!);
		totalDifference += difference;
		if (difference > 64) largeDifferences++;
	}

	return totalDifference / pixelsA.length <= 2 && largeDifferences <= pixelsA.length / 10000;
}

/**
 * Évite qu'une image passée par l'éditeur soit plus lourde que l'image d'origine :
 * - si son contenu est celui de l'original, l'original est renvoyé tel quel ;
 * - en JPEG ou WebP, elle est ré-encodée en baissant la qualité par paliers jusqu'à passer sous le poids de l'original.
 * Une image PNG réellement recadrée est gardée telle quelle : le PNG est sans perte.
 */
export async function preventHeavierImage(edited: File, original: File): Promise<File> {
	if (edited.size <= original.size) return edited;

	try {
		const [editedImage, originalImage] = await Promise.all([createImageBitmap(edited), createImageBitmap(original)]);

		if (haveSameContent(editedImage, originalImage)) return original;
		if (!LOSSY_TYPES.includes(edited.type)) return edited;

		const canvas = drawToCanvas(editedImage);
		let smallest: Blob = edited;
		for (const quality of LOSSY_QUALITIES) {
			const blob = await canvasToBlob(canvas, edited.type, quality);
			if (blob.type !== edited.type) break; // Format non supporté par le navigateur

			if (blob.size < smallest.size) smallest = blob;
			if (blob.size <= original.size) break;
		}

		return smallest === edited ? edited : new File([smallest], edited.name, { type: smallest.type, lastModified: Date.now() });
	} catch (e) {
		return edited;
	}
}
